// Organisation options (contracts/options.md), the queue configuration switch
// (contracts/basic-config.md) and how the Options page groups them.

/** One switch as GET /options answers it: `on` is chosen, `effective` is within the licence. */
export interface OrgOption {
  key: string
  label: string
  on: boolean
  effective: boolean
  /** The licence module that bounds it, when one does. */
  module?: string
}

export interface OptionsView {
  organisation: string
  options: OrgOption[]
  handoffLabel: string
}

export interface OptionsPatch {
  options?: Record<string, boolean>
  handoffLabel?: string
}

/** The options the screens obey, as /auth/me sends them. */
export type OptionKey = 'handoff' | 'outOfTurnCall' | 'callStartsServing' | 'onlineBooking' | 'priorityTickets'

/**
 * Whether the screens should offer an option. A server too old to send options
 * sends none, and then everything stays on; in a map, a missing key is off.
 */
export function optionOn(identity: Pick<Identity, 'options'> | null | undefined, key: OptionKey): boolean {
  const options = identity?.options
  return !options || options[key] === true
}

export type ConfigMode = 'basic' | 'advanced'

export interface ConfigProblem {
  path: string
  code: string
  message: string
  args?: Record<string, string>
  warning?: boolean
}

/** GET /config/mode: basicProblems says why Basic would be refused; [] = may switch. */
export interface ModeView {
  mode: ConfigMode
  basicProblems: ConfigProblem[]
}

export type OptionArea = 'counter' | 'reception' | 'booking' | 'other'

/** Where each known option is shown; a key this build does not know goes under Other. */
const AREA: Record<string, OptionArea> = {
  handoff: 'counter',
  outOfTurnCall: 'counter',
  callStartsServing: 'counter',
  onlineBooking: 'booking',
  priorityTickets: 'reception',
}

export const AREAS: OptionArea[] = ['counter', 'reception', 'booking', 'other']

/** The options of one area, in the server's order. */
export function optionsIn(area: OptionArea, options: readonly OrgOption[]): OrgOption[] {
  return options.filter((o) => (AREA[o.key] ?? 'other') === area)
}

/** The hand-off button's name: one line, at most 24 characters (contract). */
export const HANDOFF_LABEL_MAX = 24

export function handoffLabelProblem(label: string): boolean {
  return label.length > HANDOFF_LABEL_MAX || /[\r\n]/.test(label)
}

/** Hand-off on, but no active service is its point: the counter has nowhere to send.
 *  False while the services are unknown (not yet read, or not this person's to read). */
export function handoffWithoutPoint(on: boolean, services: readonly Pick<Service, 'cashier' | 'active'>[] | null): boolean {
  return on && services !== null && !services.some((s) => s.cashier && s.active)
}

/** GET/PUT /counter-policy (contracts/counter.md): the organisation's desk rules. */
export type { CounterPolicy, BreakReason } from './types'
import type { BreakReason, Identity, Service } from './types'

export type BreakKind = 'lunch' | 'short_break' | 'task'

export const BREAK_KINDS: BreakKind[] = ['lunch', 'short_break', 'task']


/** A reason's name: 1 to 60 characters, unique in any case (the server checks again). */
export const BREAK_REASON_MAX = 60

export function reasonNameProblem(
  name: string,
  others: readonly BreakReason[],
  self?: string,
): 'empty' | 'long' | 'taken' | '' {
  const typed = name.trim()
  if (!typed) return 'empty'
  if (typed.length > BREAK_REASON_MAX) return 'long'
  const lower = typed.toLocaleLowerCase()
  return others.some((r) => r.id !== self && r.name.trim().toLocaleLowerCase() === lower) ? 'taken' : ''
}

/** Reasons in the order staff see them. */
export function reasonsInOrder(reasons: readonly BreakReason[]): BreakReason[] {
  return [...reasons].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
}
